import { useEffect, useState } from "react";
import client, { API, formatApiErrorDetail } from "@/lib/api";
import { uploadVideoChunked } from "@/lib/upload";
import { toast } from "sonner";
import { Btn, Field, Input, Textarea } from "@/components/kit";
import { X, Sparkles, Upload, Loader2, Save, ImageIcon, FileVideo, Trash2 } from "lucide-react";

function PostImage({ mediaId }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!mediaId) { setUrl(null); return; }
    let active = true;
    client.get(`/media/${mediaId}/file`, { responseType: "blob" })
      .then((r) => { if (active) setUrl(URL.createObjectURL(r.data)); }).catch(() => {});
    return () => { active = false; };
  }, [mediaId]);
  if (!url) return (
    <div className="aspect-square w-full rounded-xl bg-slate-100 flex flex-col items-center justify-center text-slate-300 gap-2" data-testid="editor-image-empty">
      <ImageIcon size={34} /><span className="text-xs text-slate-400">Sem arte ainda</span>
    </div>
  );
  return <img src={url} alt="arte do post" className="aspect-square w-full object-cover rounded-xl border border-slate-200" data-testid="editor-image-preview" />;
}

export default function PostEditor({ item, onClose, onSaved }) {
  const [title, setTitle] = useState(item.title || "");
  const [caption, setCaption] = useState(item.caption || "");
  const [hashtags, setHashtags] = useState((item.hashtags || []).join(" "));
  const [imageId, setImageId] = useState(item.image_media_id || null);
  const [videoId, setVideoId] = useState(item.video_media_id || null);
  const [videos, setVideos] = useState([]);
  const [vidBusy, setVidBusy] = useState(false);
  const [vidProgress, setVidProgress] = useState(0);
  const [gen, setGen] = useState(false);
  const [saving, setSaving] = useState(false);
  const isVideoChannel = ["YouTube", "YouTube Shorts", "TikTok"].includes(item.channel);
  const token = localStorage.getItem("pf_token");

  useEffect(() => {
    if (!isVideoChannel) return;
    client.get("/media?kind=video").then((r) => setVideos(r.data)).catch(() => {});
  }, [isVideoChannel]);

  const parseTags = () => hashtags.split(/[\s,]+/).map((t) => t.replace(/^#/, "").trim()).filter(Boolean);

  const uploadVideo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setVidBusy(true); setVidProgress(0);
    try {
      const media = await uploadVideoChunked(file, { category: "Vídeos", brandId: item.brand_id, onProgress: setVidProgress });
      if (media?.id) { setVideoId(media.id); setVideos((v) => [media, ...v]); toast.success("Vídeo enviado"); }
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setVidBusy(false); setVidProgress(0); e.target.value = ""; }
  };

  const generateImage = async () => {
    setGen(true);
    try {
      const { data } = await client.post(`/content/${item.id}/image`);
      setImageId(data.image_media_id);
      toast.success("Arte gerada com IA (Nano Banana)");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setGen(false); }
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setGen(true);
    const fd = new FormData();
    fd.append("file", file); fd.append("category", "Posts IA"); fd.append("brand_id", item.brand_id);
    try {
      const { data } = await client.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImageId(data.id);
      toast.success("Imagem enviada");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setGen(false); e.target.value = ""; }
  };

  const save = async () => {
    setSaving(true);
    try {
      await client.put(`/content/${item.id}/edit`, { title, caption, hashtags: parseTags(), image_media_id: imageId, video_media_id: videoId });
      toast.success("Conteúdo salvo");
      onSaved && onSaved();
      onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="post-editor">
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto pf-fade-up">
        <div className="sticky top-0 bg-white border-b border-slate-100 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h3 className="font-display font-bold text-slate-900">Editar post · {item.channel}</h3>
            <p className="text-xs text-slate-500">Dia {item.day} · {item.theme}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="editor-close"><X size={20} /></button>
        </div>

        <div className="p-6 grid md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <PostImage mediaId={imageId} />
            <div className="grid grid-cols-2 gap-2">
              <Btn variant="primary" data-testid="editor-generate-image" onClick={generateImage} disabled={gen}>
                {gen ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Gerar com IA
              </Btn>
              <label className="cursor-pointer">
                <span className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 text-slate-700 text-sm font-semibold px-4 py-2.5 hover:bg-slate-50" data-testid="editor-upload-image-label"><Upload size={15} /> Enviar</span>
                <input type="file" accept="image/*" className="hidden" onChange={uploadImage} data-testid="editor-upload-image-input" />
              </label>
            </div>

            {isVideoChannel && (
              <div className="rounded-xl border border-slate-200 p-3 space-y-3" data-testid="editor-video-section">
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-700"><FileVideo size={16} className="text-[#3457d5]" /> Vídeo ({item.channel})</div>
                {videoId ? (
                  <div className="space-y-2">
                    <video src={`${API}/media/${videoId}/file?auth=${token}`} controls preload="metadata" className="w-full rounded-lg bg-black aspect-video object-contain" data-testid="editor-video-preview" />
                    <button onClick={() => setVideoId(null)} className="inline-flex items-center gap-1 text-xs text-rose-500 hover:text-rose-600" data-testid="editor-video-remove"><Trash2 size={13} /> Remover vídeo</button>
                  </div>
                ) : (
                  <p className="text-xs text-slate-400">Anexe um vídeo para publicar de verdade no YouTube/TikTok.</p>
                )}
                <label className="cursor-pointer block">
                  <span className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 text-slate-700 text-sm font-semibold px-4 py-2.5 hover:bg-slate-50" data-testid="editor-upload-video-label">
                    {vidBusy ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} {vidBusy ? (vidProgress ? `Enviando ${vidProgress}%` : "Enviando...") : "Enviar vídeo"}
                  </span>
                  <input type="file" accept="video/*" className="hidden" onChange={uploadVideo} disabled={vidBusy} data-testid="editor-upload-video-input" />
                </label>
                {vidBusy && vidProgress > 0 && (
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100"><div className="h-full pf-gradient transition-all" style={{ width: `${vidProgress}%` }} /></div>
                )}
                {videos.length > 0 && (
                  <Field label="Ou escolha da biblioteca">
                    <select data-testid="editor-video-select" value={videoId || ""} onChange={(e) => setVideoId(e.target.value || null)}
                      className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-[#3457d5] focus:outline-none">
                      <option value="">— Nenhum —</option>
                      {videos.map((v) => <option key={v.id} value={v.id}>{v.original_filename}</option>)}
                    </select>
                  </Field>
                )}
              </div>
            )}
          </div>

          <div className="space-y-4">
            <Field label="Título"><Input data-testid="editor-title" value={title} onChange={(e) => setTitle(e.target.value)} /></Field>
            <Field label="Legenda"><Textarea data-testid="editor-caption" rows={5} value={caption} onChange={(e) => setCaption(e.target.value)} /></Field>
            <Field label="Hashtags (separe por espaço)"><Input data-testid="editor-hashtags" value={hashtags} onChange={(e) => setHashtags(e.target.value)} placeholder="apoio recuperacao saudemental" /></Field>
            <div className="flex flex-wrap gap-1.5">
              {parseTags().slice(0, 12).map((t, i) => <span key={i} className="text-[11px] bg-blue-50 text-[#3457d5] rounded-full px-2 py-0.5">#{t}</span>)}
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-slate-100 px-6 py-4 flex justify-end gap-2">
          <Btn variant="ghost" onClick={onClose}>Cancelar</Btn>
          <Btn data-testid="editor-save" onClick={save} disabled={saving}>{saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Salvar</Btn>
        </div>
      </div>
    </div>
  );
}
